import React from 'react';
import { X, Package, Tag, DollarSign, Layers, TrendingUp, Edit } from 'lucide-react';

const ProductViewModal = ({ product, onClose, onEdit }) => {
    if (!product) return null;

    const getCategoryColor = (category) => {
        const colors = {
            'Produce': 'bg-blue-100 text-blue-700 border-blue-200',
            'Grains': 'bg-purple-100 text-purple-700 border-purple-200',
            'Oil': 'bg-cyan-100 text-cyan-700 border-cyan-200',
            'Meat': 'bg-red-100 text-red-700 border-red-200',
            'Spices': 'bg-orange-100 text-orange-700 border-orange-200'
        };
        return colors[category] || 'bg-gray-100 text-gray-700 border-gray-200';
    };

    const getStockStatus = (stock) => {
        if (stock > 200) return { label: 'Good Stock', color: 'text-green-600 bg-green-50 border-green-200' };
        if (stock > 100) return { label: 'Good Stock', color: 'text-blue-600 bg-blue-50 border-blue-200' };
        if (stock > 50) return { label: 'Low Stock', color: 'text-yellow-600 bg-yellow-50 border-yellow-200' };
        return { label: 'Critical', color: 'text-red-600 bg-red-50 border-red-200' };
    };

    const stockStatus = getStockStatus(product.stock);
    const profitMargin = ((product.retailPrice - product.price) / product.price * 100).toFixed(1);
    const profitPerUnit = (product.retailPrice - product.price).toFixed(2);
    const stockValue = (product.price * product.stock).toFixed(2);

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-3 animate-fade-in">
            <div className="bg-white rounded-3xl shadow-2xl w-[60%] max-h-[80vh] overflow-hidden animate-scale-in flex flex-col">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-5 py-3 relative flex-shrink-0">
                    <button
                        onClick={onClose}
                        className="absolute top-2 right-2 p-1.5 hover:bg-white/20 rounded-full transition-all"
                    >
                        <X size={18} />
                    </button>
                    <h2 className="text-lg font-bold pr-8">Product Details</h2>
                    <p className="text-blue-100 text-xs mt-0.5">View complete product information</p>
                </div>

                {/* Content */}
                <div className="flex-1 p-4 overflow-hidden">
                    <div className="grid grid-cols-12 gap-4 h-full">
                        {/* Left Column */}
                        <div className="col-span-5 space-y-2.5">
                            <div className="flex justify-center">
                                {product.image ? (
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-32 h-32 object-cover rounded-2xl shadow-lg border-2 border-gray-100"
                                    />
                                ) : (
                                    <div className="w-32 h-32 bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl shadow-lg border-2 border-gray-100 flex items-center justify-center">
                                        <Package size={48} className="text-blue-600" />
                                    </div>
                                )}
                            </div>

                            <div>
                                <h3 className="text-xl font-bold text-gray-800 mb-2">{product.name}</h3>
                                <div className="flex flex-wrap gap-1.5">
                                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${getCategoryColor(product.category)}`}>
                                        <Tag size={12} className="mr-1" />
                                        {product.category}
                                    </span>
                                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${stockStatus.color}`}>
                                        <TrendingUp size={12} className="mr-1" />
                                        {stockStatus.label}
                                    </span>
                                </div>
                                <div className="text-xs text-gray-500 mt-2">
                                    ID: <span className="font-mono font-semibold text-gray-700">#{product.id.toString().padStart(6, '0')}</span>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <div className="p-2.5 bg-gray-50 rounded-xl">
                                    <div className="flex items-center gap-1.5 text-xs text-gray-600 mb-1">
                                        <Package size={14} className="text-blue-600" />
                                        <span className="font-medium">Unit</span>
                                    </div>
                                    <div className="font-semibold text-sm text-gray-800">{product.unit}</div>
                                </div>
                                <div className="p-2.5 bg-gray-50 rounded-xl">
                                    <div className="flex items-center gap-1.5 text-xs text-gray-600 mb-1">
                                        <Layers size={14} className="text-blue-600" />
                                        <span className="font-medium">Stock</span>
                                    </div>
                                    <div className={`font-bold text-sm ${stockStatus.color.split(' ')[0]}`}>
                                        {product.stock} units
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Column */}
                        <div className="col-span-7 space-y-2.5">
                            <div className="grid grid-cols-2 gap-2.5">
                                <div className="p-3 bg-blue-50 rounded-xl border border-blue-100">
                                    <div className="flex items-center gap-1.5 text-xs text-gray-600 mb-1">
                                        <DollarSign size={14} className="text-blue-600" />
                                        <span className="font-medium">Wholesale</span>
                                    </div>
                                    <div className="text-xl font-bold text-blue-600">
                                        AED {product.price.toFixed(2)}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-0.5">{product.unit}</div>
                                </div>
                                <div className="p-3 bg-green-50 rounded-xl border border-green-100">
                                    <div className="flex items-center gap-1.5 text-xs text-gray-600 mb-1">
                                        <DollarSign size={14} className="text-green-600" />
                                        <span className="font-medium">Retail</span>
                                    </div>
                                    <div className="text-xl font-bold text-green-600">
                                        AED {product.retailPrice.toFixed(2)}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-0.5">{product.unit}</div>
                                </div>
                            </div>

                            <div className="p-4 bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl border border-blue-100">
                                <h4 className="font-bold text-sm text-gray-800 mb-2.5 flex items-center gap-1.5">
                                    <TrendingUp size={16} className="text-blue-600" />
                                    Pricing Analysis
                                </h4>
                                <div className="grid grid-cols-3 gap-2">
                                    <div className="bg-white p-2.5 rounded-lg shadow-sm border border-gray-200">
                                        <div className="text-xs text-gray-600 mb-0.5">Margin</div>
                                        <div className="text-lg font-bold text-green-600">
                                            {profitMargin}%
                                        </div>
                                    </div>
                                    <div className="bg-white p-2.5 rounded-lg shadow-sm border border-gray-200">
                                        <div className="text-xs text-gray-600 mb-0.5">Profit/Unit</div>
                                        <div className="text-lg font-bold text-blue-600">
                                            AED {profitPerUnit}
                                        </div>
                                    </div>
                                    <div className="bg-white p-2.5 rounded-lg shadow-sm border border-gray-200">
                                        <div className="text-xs text-gray-600 mb-0.5">Stock Value</div>
                                        <div className="text-lg font-bold text-purple-600">
                                            AED {stockValue}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
                                <h4 className="font-bold text-xs text-gray-800 mb-2">Additional Info</h4>
                                <div className="space-y-1.5 text-xs">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Last Updated:</span>
                                        <span className="font-semibold text-gray-800">
                                            {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Status:</span>
                                        <span className="font-semibold text-green-600">Active</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 px-4 py-2.5 bg-gray-50 flex gap-2 justify-end flex-shrink-0">
                    <button
                        onClick={onClose}
                        className="px-4 py-1.5 text-xs border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-all font-semibold"
                    >
                        Close
                    </button>
                    <button
                        onClick={() => {
                            onClose();
                            onEdit(product);
                        }}
                        className="px-4 py-1.5 text-xs bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all font-semibold shadow-md hover:shadow-lg flex items-center gap-1.5"
                    >
                        <Edit size={14} />
                        Edit Product
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProductViewModal;
